import React from 'react';
import {
  InfiniteScroll,
  ReviewCard,
  decodeHTMLEntities,
  getFormattedDateShortMonthDDYYYY,
  replaceNewLine,
  Link,
} from '@marageti/z4-lib';
import {
  Chip,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Review, ReviewStatus } from '@marageti/z4-sdk/lib/travel';
import useURLSearchQuery from '../../hooks/use-url-for-search-query';
import useReviewSearch from '../../hooks/use-review-search';
import { getRatingNumber } from '../../utils/helpers';
import ReviewsFilters from './reviews-filters';

interface DefaultSearch {
  [key: string]: string | string[];
  fullText: string;
  rating: string[];
  status: string[];
  order: string;
}

const defaultSearch: DefaultSearch = {
  fullText: '',
  rating: [],
  status: [],
  order: 'desc',
};

const getReviewStatusChip = (review: Review) => {
  if (review.status === ReviewStatus.ReviewStatusPending) {
    return (
      <Chip
        label="Draft"
        variant="outlined"
        size="small"
      />
    );
  }

  if (review.published) {
    return (
      <Chip
        label="Published"
        variant="filled"
        size="small"
        sx={{
          backgroundColor: (theme) => theme.palette.common.green25,
        }}
      />
    );
  }

  return (
    <Chip
      label="Unpublished"
      variant="outlined"
      size="small"
    />
  );
};

const InfiniteReviewsList = () => {
  const [queryValues, updateURLQuery] = useURLSearchQuery<DefaultSearch>(defaultSearch);
  const {
    fullText, rating, status, order,
  } = queryValues;

  const {
    reviews,
    error,
    hasMore,
    isLoading,
    loadMore,
    totalHits,
  } = useReviewSearch(queryValues);

  const onChangeFilter = (name: string, v: string | string[]) => {
    switch (name) {
      case 'fullText':
        updateURLQuery({ fullText: v });
        break;
      case 'rating':
        updateURLQuery({ rating: v });
        break;
      case 'status':
        updateURLQuery({ status: v });
        break;
      case 'order':
        updateURLQuery({ order: v });
        break;
      default:
        break;
    }
  };

  const formattedReviewsCount = new Intl.NumberFormat('en-US').format(totalHits);

  return (
    <>
      <ReviewsFilters
        onChange={onChangeFilter}
        order={order}
        searchValue={fullText}
        selectedRatingChips={rating}
        selectedStatusChips={status}
        disableSortBy={fullText.length > 0}
      />
      {isLoading ? (
        <Skeleton variant="rounded" sx={{ mb: 2, height: '24px', width: '100px' }} />
      ) : (
        <Typography component="p" variant="h4" sx={{ mb: 1.5 }}>
          {`${formattedReviewsCount === '10,000' ? `${formattedReviewsCount}+` : formattedReviewsCount} Result${formattedReviewsCount === '1' ? '' : 's'}`}
        </Typography>
      )}
      <Stack spacing={1}>
        <InfiniteScroll
          error={error}
          hasMoreItems={hasMore}
          isLoading={isLoading}
          loadMoreItems={loadMore}
        >
          {reviews?.flatMap((review: Review) => (
            <Link key={review.id} to={`/reviews/${review.id}`} state={{ review }}>
              <ReviewCard
                title={review.status === ReviewStatus.ReviewStatusPending ? 'Draft' : replaceNewLine(decodeHTMLEntities(review.title)) || '-'}
                titleTypographyProps={{
                  fontStyle: review.status === ReviewStatus.ReviewStatusPending ? 'italic' : undefined,
                }}
                subtitle={`${review.travelerName} • ${getFormattedDateShortMonthDDYYYY(review.createdAt)}`}
                avatar={{ src: review.travelerAvatar, name: review.travelerName }}
                rating={getRatingNumber(review.rating)}
                chips={getReviewStatusChip(review)}
              />
            </Link>
          ))}
        </InfiniteScroll>
      </Stack>
    </>
  );
};

export default InfiniteReviewsList;
